import React, { useState, useEffect } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "./utils/api";
import Layout from "./components/Layout";

// Predefined asset classes with descriptions
const PREDEFINED_CATEGORIES = [
  { name: "Bargeld", description: "Physisches Geld und sofort verfügbare Mittel", is_liquid: true },
  { name: "Girokonto", description: "Täglich verfügbares Bankkonto", is_liquid: true },
  { name: "Sparkonto", description: "Verzinstes Sparkonto", is_liquid: true },
  { name: "ETF", description: "Exchange Traded Funds - Indexfonds", is_liquid: true },
  { name: "Aktien", description: "Einzelne Unternehmensanteile", is_liquid: true },
  { name: "Obligationen", description: "Festverzinsliche Wertpapiere", is_liquid: true },
  { name: "Kryptowährungen", description: "Bitcoin, Ethereum und andere Coins", is_liquid: true },
  { name: "Edelmetalle", description: "Gold, Silber, Platin", is_liquid: true },
  { name: "3A", description: "Säule 3a - Gebundene Vorsorge", is_liquid: false },
  { name: "Immobilien", description: "Eigentum an Grundstücken und Gebäuden", is_liquid: false },
  { name: "Steuerabgrenzung", description: "Rückstellungen für Steuern", is_liquid: false },
  { name: "Lebensversicherung", description: "Kapitallebensversicherungen", is_liquid: false },
];

const PREDEFINED_CURRENCIES = [
  { code: "CHF", description: "Schweizer Franken" },
  { code: "EUR", description: "Euro" },
  { code: "USD", description: "US-Dollar" },
  { code: "GBP", description: "Britisches Pfund" },
  { code: "BTC", description: "Bitcoin" },
  { code: "ETH", description: "Ethereum" },
];

function Customize() {
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [newCatIsLiquid, setNewCatIsLiquid] = useState(true);
  const [newCur, setNewCur] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editCategory, setEditCategory] = useState(null);
  const [editCurrency, setEditCurrency] = useState(null);

  const fetchData = () => {
    Promise.all([
      apiGet("/categories").then(res => res.json()),
      apiGet("/currencies").then(res => res.json())
    ])
      .then(([cats, curs]) => {
        setCategories(cats);
        setCurrencies(curs);
        setLoading(false);
      })
      .catch(err => {
        console.error("Fehler beim Laden:", err);
        setLoading(false);
      });
  };

  useEffect(fetchData, []);

  // Check if a predefined category is enabled
  const isCategoryEnabled = (catName) => {
    return categories.some(c => c.name === catName);
  };

  // Check if a predefined currency is enabled
  const isCurrencyEnabled = (currCode) => {
    return currencies.some(c => c.code === currCode);
  };

  // Get custom categories (not in predefined list)
  const getCustomCategories = () => {
    const predefinedNames = PREDEFINED_CATEGORIES.map(c => c.name);
    return categories.filter(c => !predefinedNames.includes(c.name));
  };

  // Get custom currencies (not in predefined list)
  const getCustomCurrencies = () => {
    const predefinedCodes = PREDEFINED_CURRENCIES.map(c => c.code);
    return currencies.filter(c => !predefinedCodes.includes(c.code));
  };

  // Toggle predefined category
  const togglePredefinedCategory = (category) => {
    const existing = categories.find(c => c.name === category.name);

    if (existing) {
      // Delete it
      apiDelete(`/categories/${existing.id}`)
        .then(async res => {
          if (!res.ok) {
            const err = await res.json();
            alert(err.detail);
          } else {
            fetchData();
          }
        })
        .catch(err => console.error(err));
    } else {
      // Add it
      apiPost("/categories", { name: category.name, is_liquid: category.is_liquid })
        .then(res => {
          if (!res.ok) throw new Error("Fehler beim Hinzufügen");
          return res.json();
        })
        .then(() => {
          fetchData();
        })
        .catch(err => setError(err.message));
    }
  };

  // Toggle predefined currency
  const togglePredefinedCurrency = (currency) => {
    const existing = currencies.find(c => c.code === currency.code);

    if (existing) {
      // Delete it
      apiDelete(`/currencies/${existing.id}`)
        .then(async res => {
          if (!res.ok) {
            const err = await res.json();
            alert(err.detail);
          } else {
            fetchData();
          }
        })
        .catch(err => console.error(err));
    } else {
      // Add it
      apiPost("/currencies", { code: currency.code })
        .then(res => {
          if (!res.ok) throw new Error("Fehler beim Hinzufügen");
          return res.json();
        })
        .then(() => {
          fetchData();
        })
        .catch(err => setError(err.message));
    }
  };

  const addCategory = () => {
    if (!newCat.trim()) return;
    apiPost("/categories", { name: newCat, is_liquid: newCatIsLiquid })
      .then(res => {
        if (!res.ok) throw new Error("Fehler beim Hinzufügen");
        return res.json();
      })
      .then(() => {
        setNewCat("");
        setNewCatIsLiquid(true);
        setError("");
        fetchData();
      })
      .catch(err => setError(err.message));
  };

  const saveEditCategory = () => {
    if (!editCategory.name.trim()) return;
    apiPut(`/categories/${editCategory.id}`, { name: editCategory.name, is_liquid: editCategory.is_liquid })
      .then(res => {
        if (!res.ok) throw new Error("Fehler beim Aktualisieren");
        return res.json();
      })
      .then(() => {
        setEditCategory(null);
        setError("");
        fetchData();
      })
      .catch(err => setError(err.message));
  };

  const deleteCategory = (id, categoryName) => {
    if (!window.confirm(`Kategorie "${categoryName}" wirklich löschen?`)) return;
    apiDelete(`/categories/${id}`)
      .then(async res => {
        if (!res.ok) {
          const err = await res.json();
          alert(err.detail || "Fehler beim Löschen");
        } else {
          fetchData();
        }
      })
      .catch(err => {
        console.error(err);
        alert("Fehler beim Löschen der Kategorie");
      });
  };

  const addCurrency = () => {
    if (!newCur.trim()) return;
    apiPost("/currencies", { code: newCur })
      .then(res => {
        if (!res.ok) throw new Error("Fehler beim Hinzufügen");
        return res.json();
      })
      .then(() => {
        setNewCur("");
        setError("");
        fetchData();
      })
      .catch(err => setError(err.message));
  };

  const saveEditCurrency = () => {
    if (!editCurrency.code.trim()) return;
    apiPut(`/currencies/${editCurrency.id}`, { code: editCurrency.code })
      .then(res => {
        if (!res.ok) throw new Error("Fehler beim Aktualisieren");
        return res.json();
      })
      .then(() => {
        setEditCurrency(null);
        setError("");
        fetchData();
      })
      .catch(err => setError(err.message));
  };

  const deleteCurrency = (id, currencyCode) => {
    if (!window.confirm(`Währung "${currencyCode}" wirklich löschen?`)) return;
    apiDelete(`/currencies/${id}`)
      .then(async res => {
        if (!res.ok) {
          const err = await res.json();
          alert(err.detail || "Fehler beim Löschen");
        } else {
          fetchData();
        }
      })
      .catch(err => {
        console.error(err);
        alert("Fehler beim Löschen der Währung");
      });
  };

  if (loading) {
    return (
      <Layout title="Customizing">
        <div className="card">
          <p>Lade Optionen...</p>
        </div>
      </Layout>
    );
  }

  const customCategories = getCustomCategories();
  const customCurrencies = getCustomCurrencies();

  return (
    <Layout title="Asset-Verwaltung">
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Edit Category Form */}
      {editCategory && (
        <div className="card" style={{ maxWidth: "700px", margin: "0 auto var(--space-6)" }}>
          <h3 className="card-header">Kategorie bearbeiten</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Name</label>
              <input
                type="text"
                value={editCategory.name}
                onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Liquidität</label>
              <select
                value={editCategory.is_liquid ? "liquid" : "non-liquid"}
                onChange={(e) => setEditCategory({ ...editCategory, is_liquid: e.target.value === "liquid" })}
              >
                <option value="liquid">💧 Flüssig</option>
                <option value="non-liquid">🔒 Nicht-flüssig</option>
              </select>
            </div>
          </div>
          <div className="flex gap-md">
            <button className="btn btn-primary" onClick={saveEditCategory}>Speichern</button>
            <button className="btn btn-secondary" onClick={() => setEditCategory(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Edit Currency Form */}
      {editCurrency && (
        <div className="card" style={{ maxWidth: "700px", margin: "0 auto var(--space-6)" }}>
          <h3 className="card-header">Währung bearbeiten</h3>
          <div className="form-group">
            <label className="form-label">Code</label>
            <input
              type="text"
              value={editCurrency.code}
              onChange={(e) => setEditCurrency({ ...editCurrency, code: e.target.value })}
              required
            />
          </div>
          <div className="flex gap-md">
            <button className="btn btn-primary" onClick={saveEditCurrency}>Speichern</button>
            <button className="btn btn-secondary" onClick={() => setEditCurrency(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      <div className="card" style={{ maxWidth: "700px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "var(--space-8)" }}>
          <h2 style={{ fontSize: "1.5rem", marginBottom: "var(--space-2)" }}>
            Verwalten Sie Ihre Asset-Klassen
          </h2>
          <p style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-sm)" }}>
            Aktivieren Sie die Asset-Typen, die Sie tracken möchten
          </p>
        </div>

        {/* Predefined Categories */}
        <div style={{ marginBottom: "var(--space-8)" }}>
          <h3 style={{
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "var(--text-secondary)",
            marginBottom: "var(--space-4)",
            fontWeight: "600"
          }}>
            Standard Asset-Kategorien
          </h3>

          <div className="toggle-list">
            {PREDEFINED_CATEGORIES.map((cat) => {
              const existingCategory = categories.find(c => c.name === cat.name);
              const isEnabled = isCategoryEnabled(cat.name);

              return (
                <div key={cat.name} className="toggle-item">
                  <div className="toggle-item-content">
                    <div className="toggle-item-title">
                      {cat.name} {cat.is_liquid ? "💧" : "🔒"}
                    </div>
                    <div className="toggle-item-description">{cat.description}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    {isEnabled && existingCategory ? (
                      <>
                        <button
                          className="btn-icon btn-icon-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditCategory(existingCategory);
                          }}
                          title="Bearbeiten"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          className="btn-icon btn-icon-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCategory(existingCategory.id, cat.name);
                          }}
                          title="Löschen"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M5.33301 4.00004V2.66671C5.33301 2.31309 5.47348 1.97395 5.72353 1.7239C5.97358 1.47385 6.31272 1.33337 6.66634 1.33337H9.33301C9.68663 1.33337 10.0258 1.47385 10.2758 1.7239C10.5259 1.97395 10.6663 2.31309 10.6663 2.66671V4.00004M12.6663 4.00004V13.3334C12.6663 13.687 12.5259 14.0261 12.2758 14.2762C12.0258 14.5262 11.6866 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2762C3.47348 14.0261 3.33301 13.687 3.33301 13.3334V4.00004H12.6663Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </>
                    ) : null}
                    <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => togglePredefinedCategory(cat)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom Categories */}
        {customCategories.length > 0 && (
          <div style={{ marginBottom: "var(--space-8)" }}>
            <h3 style={{
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--text-secondary)",
              marginBottom: "var(--space-4)",
              fontWeight: "600"
            }}>
              Eigene Kategorien
            </h3>

            <div className="toggle-list">
              {customCategories.map((c) => (
                <div key={c.id} className="toggle-item">
                  <div className="toggle-item-content">
                    <div className="toggle-item-title">
                      {c.name} {c.is_liquid ? "💧" : "🔒"}
                    </div>
                    <div className="toggle-item-description">Individuelle Kategorie</div>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <button
                      className="btn-icon btn-icon-primary"
                      onClick={() => setEditCategory(c)}
                      title="Bearbeiten"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => deleteCategory(c.id, c.name)}
                      title="Löschen"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.33301 4.00004V2.66671C5.33301 2.31309 5.47348 1.97395 5.72353 1.7239C5.97358 1.47385 6.31272 1.33337 6.66634 1.33337H9.33301C9.68663 1.33337 10.0258 1.47385 10.2758 1.7239C10.5259 1.97395 10.6663 2.31309 10.6663 2.66671V4.00004M12.6663 4.00004V13.3334C12.6663 13.687 12.5259 14.0261 12.2758 14.2762C12.0258 14.5262 11.6866 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2762C3.47348 14.0261 3.33301 13.687 3.33301 13.3334V4.00004H12.6663Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Custom Category */}
        <div style={{ marginBottom: "var(--space-8)", padding: "var(--space-6)", background: "var(--bg-secondary)", borderRadius: "var(--radius-lg)" }}>
          <h3 style={{
            fontSize: "0.875rem",
            marginBottom: "var(--space-4)",
            fontWeight: "600"
          }}>
            Eigene Kategorie hinzufügen
          </h3>
          <div className="flex gap-md mb-sm">
            <input
              type="text"
              placeholder="z.B. Private Equity, Sammlungen..."
              value={newCat}
              onChange={e => setNewCat(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && addCategory()}
              style={{ flex: 1 }}
            />
            <button className="btn btn-success" onClick={addCategory}>Hinzufügen</button>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
            <input
              type="checkbox"
              checked={newCatIsLiquid}
              onChange={e => setNewCatIsLiquid(e.target.checked)}
            />
            Als flüssige Mittel markieren (💧)
          </label>
        </div>

        {/* Predefined Currencies */}
        <div style={{ marginBottom: "var(--space-8)" }}>
          <h3 style={{
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "var(--text-secondary)",
            marginBottom: "var(--space-4)",
            fontWeight: "600"
          }}>
            Standard Währungen
          </h3>

          <div className="toggle-list">
            {PREDEFINED_CURRENCIES.map((cur) => {
              const existingCurrency = currencies.find(c => c.code === cur.code);
              const isEnabled = isCurrencyEnabled(cur.code);

              return (
                <div key={cur.code} className="toggle-item">
                  <div className="toggle-item-content">
                    <div className="toggle-item-title">{cur.code}</div>
                    <div className="toggle-item-description">{cur.description}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    {isEnabled && existingCurrency ? (
                      <>
                        <button
                          className="btn-icon btn-icon-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditCurrency(existingCurrency);
                          }}
                          title="Bearbeiten"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          className="btn-icon btn-icon-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCurrency(existingCurrency.id, cur.code);
                          }}
                          title="Löschen"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M5.33301 4.00004V2.66671C5.33301 2.31309 5.47348 1.97395 5.72353 1.7239C5.97358 1.47385 6.31272 1.33337 6.66634 1.33337H9.33301C9.68663 1.33337 10.0258 1.47385 10.2758 1.7239C10.5259 1.97395 10.6663 2.31309 10.6663 2.66671V4.00004M12.6663 4.00004V13.3334C12.6663 13.687 12.5259 14.0261 12.2758 14.2762C12.0258 14.5262 11.6866 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2762C3.47348 14.0261 3.33301 13.687 3.33301 13.3334V4.00004H12.6663Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </>
                    ) : null}
                    <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => togglePredefinedCurrency(cur)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom Currencies */}
        {customCurrencies.length > 0 && (
          <div style={{ marginBottom: "var(--space-8)" }}>
            <h3 style={{
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--text-secondary)",
              marginBottom: "var(--space-4)",
              fontWeight: "600"
            }}>
              Eigene Währungen
            </h3>

            <div className="toggle-list">
              {customCurrencies.map((cur) => (
                <div key={cur.id} className="toggle-item">
                  <div className="toggle-item-content">
                    <div className="toggle-item-title">{cur.code}</div>
                    <div className="toggle-item-description">Individuelle Währung</div>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <button
                      className="btn-icon btn-icon-primary"
                      onClick={() => setEditCurrency(cur)}
                      title="Bearbeiten"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => deleteCurrency(cur.id, cur.code)}
                      title="Löschen"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.33301 4.00004V2.66671C5.33301 2.31309 5.47348 1.97395 5.72353 1.7239C5.97358 1.47385 6.31272 1.33337 6.66634 1.33337H9.33301C9.68663 1.33337 10.0258 1.47385 10.2758 1.7239C10.5259 1.97395 10.6663 2.31309 10.6663 2.66671V4.00004M12.6663 4.00004V13.3334C12.6663 13.687 12.5259 14.0261 12.2758 14.2762C12.0258 14.5262 11.6866 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2762C3.47348 14.0261 3.33301 13.687 3.33301 13.3334V4.00004H12.6663Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Custom Currency */}
        <div style={{ padding: "var(--space-6)", background: "var(--bg-secondary)", borderRadius: "var(--radius-lg)" }}>
          <h3 style={{
            fontSize: "0.875rem",
            marginBottom: "var(--space-4)",
            fontWeight: "600"
          }}>
            Eigene Währung hinzufügen
          </h3>
          <div className="flex gap-md">
            <input
              type="text"
              placeholder="z.B. JPY, CNY, XRP..."
              value={newCur}
              onChange={e => setNewCur(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && addCurrency()}
              style={{ flex: 1 }}
            />
            <button className="btn btn-success" onClick={addCurrency}>Hinzufügen</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Customize;
